import * as random from "../../../common/random.ts";
import { EXECUTIVE_SCENARIO } from "../../../common/executiveChallenge.ts";
import { idb } from "../../db/index.ts";
import setGameAttributes from "./setGameAttributes.ts";

// Turn a freshly-built real-2026 league into an Executive Challenge save. Runs
// right after the league is created with the user on the challenge team (tid),
// BEFORE any post-build phase advance, so every random draw from here on comes
// from the shared per-challenge stream.
//
// It (1) seeds the deterministic PRNG from the per-challenge seed — same code ⇒
// same seed ⇒ byte-identical injuries/development/lottery for every entrant —
// and (2) persists the challenge metadata. The seed is applied to the live
// session immediately AND persisted so reloads restore the identical stream
// (see loadGameAttributes / Cache.flush).
//
// alreadySeeded: the caller seeded the stream itself, earlier — enterChallenge
// does this BEFORE the league build, because the build consumes random draws
// that shape the world (ratings fuzz, mood, future draft classes). In that
// case we persist the RUNNING state instead of resetting to the seed, so the
// post-build sim doesn't replay the build's stream. Every entrant runs the
// identical build from the identical seed, so the running state matches too.
//
// Unlike the old campaign contests there is NO rosterLock: making moves is the
// whole point — the world is frozen, the front office isn't.
const applyExecutiveChallenge = async (
	args: {
		code: string;
		seed: string;
		tid: number;
		role: "host" | "guest";
		playerId: string;
	},
	opts?: { alreadySeeded?: boolean },
): Promise<void> => {
	const s = EXECUTIVE_SCENARIO;

	if (!opts?.alreadySeeded) {
		// Seed the live session now so all subsequent generation/sim is deterministic.
		random.setSeed(args.seed);
	}

	// Snapshot the frozen starting roster — the ledger's "yours to lose" set.
	// Identical for every entrant of a code (identical world), so it changes
	// nothing about determinism; it just spares reconstructing history later.
	const roster = await idb.cache.players.indexGetAll("playersByTid", args.tid);
	const startingPids = roster.map((p) => p.pid).sort((a, b) => a - b);

	await setGameAttributes({
		prngSeed: args.seed,
		prngState: random.getState(),
		challenge: {
			scenarioId: s.id,
			code: args.code,
			seed: args.seed,
			tid: args.tid,
			role: args.role,
			playerId: args.playerId,
			startSeason: s.startSeason,
			startPhase: s.startPhase,
			endSeason: s.endSeason,
			endPhase: s.endPhase,
			startingPids,
			status: "active",
		},
	});
};

export default applyExecutiveChallenge;
